<template>
  <div :data-cy="dataCy">
    <tree
      ref="tree"
      :data="[unit]"
      @item-click="onClickNode"
      @item-open-in-new-tab="onOpenInNewTab"
      @item-toggle="onToggleItem"
      @item-paginate="onPaginateItem"
      @item-drag-start="onDragStart"
      @item-drag-end="onDragEnd"
      @item-mouse-over="onItemMouseover"
      :cbk="addItemsWhenDragStart"
      :mode="mode"
      :role="role"
    />
  </div>
</template>

<script>
import Tree from "@/components/Tree";
import { debounce } from "lodash";
import useVideo from "./composables/video";
import useVoIP from "./composables/voIP";
import useActuators from "./composables/actuators";
import useGenericDevices from "./composables/genericDevices";
import useSensors from "./composables/sensors";
import useFacility from "./composables/facility";
import useTenant from "./composables/tenant";
import useAlarmsParents from "./composables/alarmsBridgeParents";
import useCameraMap from "./composables/cameraMap";
import { getCurrentInstance } from "@vue/composition-api";
import store from "@/store";

export default {
  name: "SubsystemTree",
  components: {
    Tree,
  },
  props: {
    unit: Object,
    mode: {
      type: String,
      default: "widget",
    },
    role: {
      type: String,
      default: "disabled",
    },
    dataCy: {
      type: String,
      default: undefined,
    },
  },

  setup(props, { emit }) {
    const video = useVideo();
    const voip = useVoIP();
    const actuators = useActuators();
    const generic_devices = useGenericDevices();
    const sensors = useSensors();
    const facility = useFacility();
    const tenant = useTenant();
    const alarms_bridge = useAlarmsParents();
    const camera_map = useCameraMap();
    const vm = getCurrentInstance().proxy;

    const tags = {
      video, // video
      voip,
      actuators,
      generic_devices,
      sensors,
      facility,
      tenant,
      alarms_bridge,
      "camera-map": camera_map,
    };

    const requestFacilities = (item, url) => {
      item.loading = true;
      store.dispatch("grid/getFacilitiesByNode", { nodeId: item.id, url });
    };

    const loadFacilities = item => {
      if (
        !item.facilitiesUrl ||
        (Array.isArray(item.children) && item.children.length > 0) ||
        item.loading
      ) {
        return false;
      }

      requestFacilities(item, item.facilitiesUrl);
      return true;
    };

    const loadFacilitiesPage = (item, page) => {
      if (!item.facilitiesUrl || item.loading) return false;
      requestFacilities(item, `${item.facilitiesUrl}&page[n]=${page}`);
      return true;
    };

    const onPaginateItem = (_, item, page) => {
      if (item) loadFacilitiesPage(item, page);
    };

    const onToggleItem = debounce(function (_, item) {
      if (!item) return;
      emit("toggle-item", { item, unit: props.unit });
      if (item.opened && loadFacilities(item)) return;
      const isOnCloseAlarmBridge = item.tag === "alarms_bridge" && item.type === "parent" && item.children.length && !item.opened;
      const isOnOpenAlarmBridge = item.tag === "alarms_bridge" && item.type === "parent" && item.children.length && item.opened;
      const alarmBridgeTree = store.getters["grid/getAlarmBridgeTree"];
      const existsAlarmBridgeItem = alarmBridgeTree.some(alarmBrige => alarmBrige.id === item.id);
      if (isOnCloseAlarmBridge) {
        onCloseAlarmBridge(item)
        return;
      }
      if (isOnOpenAlarmBridge && existsAlarmBridgeItem) {
        onOpenAlarmBridge(item)
        return;
      }

      const tagHandler = tags[item.tag];
      if (
        item.children &&
        item.children[0] &&
        item.children[0].type === "parent" &&
        tagHandler &&
        Object.prototype.hasOwnProperty.call(tagHandler, "setChildrenData")
      ) {
        return tagHandler.setChildrenData(item);
      }
    });

    const onOpenAlarmBridge = (item) => {
      const alarmBridgeTree = store.getters["grid/getAlarmBridgeTree"];
      const alarmBridgeItem = alarmBridgeTree.find(alarmBrige => alarmBrige.id === item.id);
      item.children = alarmBridgeItem.data;
      store.commit("grid/removeFromAlarmBridgeTree", item);
    }

    const onCloseAlarmBridge = (item) => {
      const itemCopy = {...item}
      const emptyChildren = store.getters["grid/getDefaultItemOfTree"];
      item.children = [emptyChildren];
      store.commit("grid/addToAlarmBridgeTree", itemCopy);
    }

    //build the url with the unit and view type we use this logic to open group
    const buildInicioUrlWithUnitAndView = (unitId, viewType, viewId) => {
      const base = `${window.location.origin}${window.location.pathname}`;
      const hash = `/inicio?unit=${encodeURIComponent(unitId)}&viewType=${encodeURIComponent(viewType)}`;
      if (viewId != null && viewId !== "") {
        return `${base}#${hash}&view=${encodeURIComponent(viewId)}`;
      }
      return `${base}#${hash}`;
    };

    //open the group in a new tab with the paramas of the view
    const onOpenInNewTab = (_, item, event) => {
      if (event) {
        event.preventDefault();
      }
      const unitId = item.unit_data?.id ?? item.unit_data?.id_unit ?? props.unit?.id ?? item.id;
      if (unitId == null) return;
      const viewType = item.type === "action"
        ? (item.tag || "")
        : (item.tag === "facility" ? "unit-live" : (item.tag === "layout" ? "layout" : ""));
      const viewId = item.id != null ? String(item.id) : "";
      if (!viewType) return;
      if (viewType === "layout" && !viewId) return;
      const url = buildInicioUrlWithUnitAndView(unitId, viewType, viewId);
      window.open(url, "_blank", "noopener,noreferrer");
    };

    const onClickNode = debounce(async function (_, item) {
      if (item.tag === "facility") {
        store.commit("grid/setFocusedFacility", item);
      } else {
        item.selected = false;
      }
      loadFacilities(item);
      if (item.type === "action") {
        for (const tag in tags) {
          if (tag === 'alarms_bridge') {
            tags[tag].actions(item, vm, props.unit.id);
          } else {
            tags[tag].actions(item, vm);
          }
        }
      }
    }, 200);

    const onDragStart = (item) => {
      for (const tag in tags) {
        tags[tag].actions(item, vm, true);
      }
    };

    const onItemMouseover = debounce(async function (_, item) {
      if (item.type === "action" ) {
        for (const tag in tags) {
          if (tags[tag].hasOwnProperty("tooltipAction")) {
            tags[tag].tooltipAction(item, vm);
          }
        }
      }
    });

    // Only needed in custom groups
    const addItemsWhenDragStart = (item) => {
      if (item.tag === "parent" && item.children.length) {
        item.children.forEach((itm) => {
          for (const _ in tags) {
            if (itm.children[0] && itm.children[0].type === "parent") {
              itm.children = [];
              if (tags[itm.tag].hasOwnProperty("setChildrenData")) {
                tags[itm.tag].setChildrenData(itm);
              }
            }
          }
        });
      } else {
        for (const _ in tags) {
          if (item.children[0] && item.children[0].type === "parent") {
            item.children = [];
            if (tags[item.tag].hasOwnProperty("setChildrenData")) {
              tags[item.tag].setChildrenData(item);
            }
          }
        }
      }

      // return item;
    };

    const onDragEnd = () => {
      store.dispatch("grid/resetSlotSelection");
    };

    return {
      onToggleItem,
      onPaginateItem,
      onClickNode,
      onOpenInNewTab,
      onDragStart,
      onDragEnd,
      addItemsWhenDragStart,
      onItemMouseover,
    };
  },
};
</script>

<style lang="scss">
.tree {
  .tree-anchor {
    i.no-icon {
      display: none;
    }
  }
}
</style>
